import { useState, useEffect, useRef, type FormEvent } from 'react';
import { api } from '../services/api';
import './TiposEtapaCRUD.css';

interface TipoEtapa {
  id: string;
  nome: string;
  descricao: string;
  categoria: 'projeto' | 'obra';
  ativo: boolean;
  projetosCount: number;
}

interface FormErrors {
  nome?: string;
  descricao?: string;
}

function TiposEtapaCRUD() {
  const [tipos, setTipos] = useState<TipoEtapa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [confirmModal, setConfirmModal] = useState<{ tipo: TipoEtapa; action: 'ativar' | 'desativar' } | null>(null);

  // Form/modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [formNome, setFormNome] = useState('');
  const [formDescricao, setFormDescricao] = useState('');
  const [formCategoria, setFormCategoria] = useState<'projeto' | 'obra'>('obra');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const fetchTipos = async () => {
    try {
      setLoading(true);
      const data = await api.get('/tipos-etapa');
      setTipos(data);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar tipos de etapa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTipos();
  }, []);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formNome.trim()) {
      errors.nome = 'Nome é obrigatório';
    } else if (formNome.length > 100) {
      errors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formDescricao.length > 255) {
      errors.descricao = 'Descrição deve ter no máximo 255 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    setError('');

    try {
      if (editingId) {
        await api.put(`/tipos-etapa/${editingId}`, { nome: formNome, descricao: formDescricao, categoria: formCategoria });
      } else {
        await api.post('/tipos-etapa', { nome: formNome, descricao: formDescricao, categoria: formCategoria });
      }
      closeModal();
      await fetchTipos();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar tipo de etapa');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormNome('');
    setFormDescricao('');
    setFormCategoria('obra');
    setFormErrors({});
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (tipo: TipoEtapa) => {
    setEditingId(tipo.id);
    setFormNome(tipo.nome);
    setFormDescricao(tipo.descricao || '');
    setFormCategoria(tipo.categoria || 'obra');
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleToggleStatus = (tipo: TipoEtapa) => {
    setConfirmModal({
      tipo,
      action: tipo.ativo ? 'desativar' : 'ativar',
    });
  };

  const confirmToggleStatus = async () => {
    if (!confirmModal) return;
    try {
      await api.patch(`/tipos-etapa/${confirmModal.tipo.id}/status`, { ativo: !confirmModal.tipo.ativo });
      setConfirmModal(null);
      await fetchTipos();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao alterar status');
      setConfirmModal(null);
    }
  };

  // Filtered and sorted
  const filtrados = tipos
    .filter((t) => {
      const matchCategoria = !filtroCategoria || t.categoria === filtroCategoria;
      const matchTexto =
        !filtroTexto ||
        t.nome.toLowerCase().includes(filtroTexto.toLowerCase()) ||
        (t.descricao && t.descricao.toLowerCase().includes(filtroTexto.toLowerCase()));
      return matchCategoria && matchTexto;
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  const getCategoriaBadgeClass = (categoria: string): string => {
    return categoria === 'projeto'
      ? 'tipos-badge tipos-badge--projeto'
      : 'tipos-badge tipos-badge--obra';
  };

  if (loading) {
    return (
      <div className="tipos-etapa-crud">
        <div className="tipos-etapa-crud-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tipos-etapa-crud">
      <div className="tipos-etapa-crud-container">
        <header className="tipos-etapa-crud-header">
          <div className="tipos-etapa-crud-header__info">
            <h1>Tipos de Etapa</h1>
            <p className="tipos-etapa-crud-subtitle">Gerencie o catálogo de tipos de etapa</p>
          </div>
          <button className="tipos-btn-novo" onClick={openCreateModal}>
            ➕ Novo Tipo
          </button>
        </header>

        {error && (
          <p style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>{error}</p>
        )}

        {/* Filters */}
        <div className="tipos-filtros">
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            aria-label="Buscar tipos de etapa"
          />
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            aria-label="Filtrar por categoria"
          >
            <option value="">Todas as categorias</option>
            <option value="obra">Obra</option>
            <option value="projeto">Projeto</option>
          </select>
        </div>

        {/* Table */}
        <div className="tipos-table-container">
          <table className="tipos-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={5} className="tipos-table__empty">
                    Nenhum tipo de etapa encontrado.
                  </td>
                </tr>
              ) : (
                filtrados.map((tipo) => (
                  <tr key={tipo.id} className="tipos-table__row">
                    <td className="tipos-table__cell-name">{tipo.nome}</td>
                    <td className="tipos-table__cell-desc">{tipo.descricao || '—'}</td>
                    <td>
                      <span className={getCategoriaBadgeClass(tipo.categoria)}>
                        {tipo.categoria === 'projeto' ? 'Projeto' : 'Obra'}
                      </span>
                    </td>
                    <td>
                      <span className={`tipos-badge-status ${tipo.ativo ? 'tipos-badge-status--ativo' : 'tipos-badge-status--inativo'}`}>
                        {tipo.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="tipos-table__actions">
                        <button
                          className="tipos-table__action-btn"
                          onClick={() => openEditModal(tipo)}
                          aria-label={`Editar ${tipo.nome}`}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="tipos-table__action-btn"
                          onClick={() => handleToggleStatus(tipo)}
                          aria-label={`${tipo.ativo ? 'Desativar' : 'Ativar'} ${tipo.nome}`}
                          title={tipo.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {tipo.ativo ? '🚫' : '✅'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {modalOpen && (
          <div className="tipos-modal-overlay" onClick={handleOverlayClick}>
            <div className="tipos-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <div className="tipos-modal__header">
                <h2>{editingId ? 'Editar Tipo de Etapa' : 'Novo Tipo de Etapa'}</h2>
                <button className="tipos-modal__close" onClick={closeModal} aria-label="Fechar">
                  ✕
                </button>
              </div>
              <form onSubmit={handleSubmit} noValidate>
                <div className="tipos-modal__body">
                  <div className="form-group">
                    <label htmlFor="tipo-nome">Nome</label>
                    <input
                      id="tipo-nome"
                      type="text"
                      value={formNome}
                      onChange={(e) => setFormNome(e.target.value)}
                      maxLength={100}
                      placeholder="Ex: Fundação"
                      className={formErrors.nome ? 'error' : ''}
                    />
                    <span className="form-error">{formErrors.nome || ''}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tipo-descricao">Descrição</label>
                    <textarea
                      id="tipo-descricao"
                      value={formDescricao}
                      onChange={(e) => setFormDescricao(e.target.value)}
                      maxLength={255}
                      placeholder="Descrição opcional"
                      className={formErrors.descricao ? 'error' : ''}
                    />
                    <span className="form-error">{formErrors.descricao || ''}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tipo-categoria">Categoria</label>
                    <select
                      id="tipo-categoria"
                      value={formCategoria}
                      onChange={(e) => setFormCategoria(e.target.value as 'projeto' | 'obra')}
                    >
                      <option value="obra">Obra</option>
                      <option value="projeto">Projeto</option>
                    </select>
                  </div>
                </div>

                <div className="tipos-modal__footer">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Salvando...' : editingId ? 'Salvar Alterações' : 'Cadastrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <div className="tipos-modal-overlay" onClick={() => setConfirmModal(null)}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Confirmar {confirmModal.action === 'ativar' ? 'Ativação' : 'Desativação'}</h3>
              <p>
                Deseja {confirmModal.action} o tipo de etapa <strong>{confirmModal.tipo.nome}</strong>?
              </p>
              <div className="confirm-modal-actions">
                <button className="btn-secondary" onClick={() => setConfirmModal(null)}>
                  Cancelar
                </button>
                <button className="btn-primary" onClick={confirmToggleStatus}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TiposEtapaCRUD;
